import type { StudentClassification } from './studentClassification';
import type { ProjectIdentity } from './project';

export type StudentOrigin = 'portal' | 'app' | 'checkout_screen';

export const STUDENT_ORIGIN_LABELS: Record<StudentOrigin, string> = {
  portal: 'Portal',
  app: 'App',
  checkout_screen: 'Checkout',
};

export function getStudentOriginLabel(
  origin: StudentOrigin | string | null | undefined,
): string {
  if (!origin) {
    return '—';
  }

  if (origin in STUDENT_ORIGIN_LABELS) {
    return STUDENT_ORIGIN_LABELS[origin as StudentOrigin];
  }

  return origin;
}

export interface Student {
  id: number;
  created_at: string;
  updated_at: string;
  user_id: string | null;
  full_name: string;
  email?: string | null;
  cpf: string | null;
  birth_date?: string | null;
  height_m?: number | null;
  weight_kg?: number | null;
  joined_at?: string | null;
  instagram_username?: string | null;
  is_inactive?: boolean | null;
  is_foreign_student?: boolean;
  is_trade_agreement?: boolean;
  coach_id?: number | null;
  coach_name?: string | null;
  origin?: StudentOrigin | null;
  classification?: StudentClassification;
  project?: ProjectIdentity | null;
  is_reactivated?: boolean;
}

export interface CreateStudentPayload {
  user_id: string | null;
  full_name: string;
  email?: string | null;
  cpf: string | null;
  birth_date?: string | null;
  joined_at?: string | null;
  instagram_username?: string | null;
  height_m?: number | null;
  weight_kg?: number | null;
  is_foreign_student?: boolean;
  is_trade_agreement?: boolean;
}

export interface UpdateStudentPayload {
  full_name?: string;
  email?: string | null;
  cpf?: string | null;
  birth_date?: string | null;
  joined_at?: string | null;
  instagram_username?: string | null;
  height_m?: number | null;
  weight_kg?: number | null;
  is_inactive?: boolean | null;
  is_foreign_student?: boolean;
  is_trade_agreement?: boolean;
}

/** Item returned by the list-paginated-students edge function */
export interface StudentListItem {
  id: number;
  full_name: string;
  email: string | null;
  cpf: string | null;
  phone: string | null;
  country_code: string | null;
  street: string | null;
  street_number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipcode: string | null;
  is_inactive: boolean | null;
  inactivation_reason: string | null; // student_inactivation_reason enum value
  inactivation_observation: string | null;
  coach_id: number | null;
  coach_name: string | null;
  origin: StudentOrigin | null;
  classification: StudentClassification | null;
  project: ProjectIdentity | null;
  is_reactivated: boolean;
  is_red_flag: boolean;
  is_sensitive: boolean;
  sensitive_reason: string | null;
  is_pregnant: boolean;
  pregnant_until: string | null;
}

/** Pagination metadata returned by paginated edge functions */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** Generic paginated response envelope */
export interface PaginatedResponse<T> {
  pagination: PaginationInfo;
  data: T[];
}
